import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SitewideRules } from "@/components/settings/SitewideRules";
import { PublicSiteSettings } from "@/components/settings/PublicSiteSettings";
import { NavLayoutSection } from "@/components/settings/NavLayoutSection";
import { Gear, CheckCircle, XCircle, Warning, ArrowsClockwise, UploadSimple, Trash, FileCsv, MagicWand, Play } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.getSettings().then(setS); runDiag(); }, []);

  const runDiag = async () => {
    setDiagLoading(true);
    try { setDiag(await api.diagnostics()); } catch { setDiag(null); }
    setDiagLoading(false);
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const r = await api.uploadCatalog(f);
      setUploadResult(r);
      toast.success(`Imported ${r.inserted} new + ${r.updated} updated sets`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onClearExtra = async () => {
    if (!window.confirm("Remove all uploaded sets (keeps the 60 demo sets)?")) return;
    try {
      const r = await api.clearNonSeeded();
      toast.success(`Removed ${r.sets_deleted} sets`);
      runDiag();
    } catch { toast.error("Failed"); }
  };

  // ----- ASIN Resolver -----
  const [resolverStats, setResolverStats] = useState(null);
  const [resolverLimit, setResolverLimit] = useState(50);
  const [resolverTheme, setResolverTheme] = useState("");
  const [resolverContinuous, setResolverContinuous] = useState(false);
  const [resolverRetryFailed, setResolverRetryFailed] = useState(false);
  const [resolverFetchBL, setResolverFetchBL] = useState(true);
  const [resolverJob, setResolverJob] = useState(null);
  const [themesList, setThemesList] = useState([]);
  const [resumableJob, setResumableJob] = useState(null);

  const loadResumable = async () => {
    try {
      const r = await api.asinResolverLastIncomplete();
      setResumableJob(r?.job || null);
    } catch { setResumableJob(null); }
  };

  const loadResolverStats = async (theme) => {
    try { setResolverStats(await api.asinResolverStats(theme || null)); } catch {}
  };

  useEffect(() => {
    loadResolverStats(resolverTheme);
    loadResumable();
    api.themes().then(setThemesList).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch theme-specific counters whenever the dropdown changes.
  useEffect(() => {
    loadResolverStats(resolverTheme);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolverTheme]);

  // Poll active resolver job + refresh resolver stats periodically
  useEffect(() => {
    if (!resolverJob || ["completed", "failed", "paused"].includes(resolverJob.status)) return;
    const t = setInterval(async () => {
      try {
        const j = await api.getScan(resolverJob.id);
        setResolverJob(j);
        loadResolverStats(resolverTheme);
        if (["completed", "failed", "paused"].includes(j.status)) {
          clearInterval(t);
          loadResolverStats(resolverTheme);
          loadResumable();
          if (j.status === "completed") toast.success(`ASIN resolver done — ${j.opportunities_found} resolved`);
          else if (j.status === "paused") toast.warning(`Resolver paused — ${j.opportunities_found} resolved. ${j.error || ""}`);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [resolverJob]);

  const runResolver = async () => {
    try {
      const j = await api.asinResolverRun({
        theme: resolverTheme || null,
        limit: Number(resolverLimit) || 50,
        skip_attempted: !resolverRetryFailed,
        retry_failed: resolverRetryFailed,
        continuous: !!resolverContinuous,
        fetch_bricklink: !!resolverFetchBL,
      });
      setResolverJob(j);
      const mode = resolverRetryFailed
        ? "Retrying previously-failed sets…"
        : (resolverContinuous
            ? `Continuous mode: resolving up to ${resolverLimit} new sets, sleeping for token refills…`
            : `Resolving up to ${resolverLimit} sets (checkpoint: skipping already-attempted)…`);
      toast.info(mode);
    } catch {
      toast.error("Resolver start failed");
    }
  };

  const cancelResolver = async () => {
    if (!resolverJob) return;
    try {
      await api.asinResolverCancel(resolverJob.id);
      toast.info("Cancelling resolver job…");
    } catch {
      toast.error("Cancel failed");
    }
  };

  const resumeResolver = async () => {
    try {
      const j = await api.asinResolverResume();
      setResolverJob(j);
      const p = j.params || {};
      toast.success(`Resuming: theme="${p.theme || 'all'}", limit=${p.limit}, BL=${p.fetch_bricklink ? "on" : "off"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No resumable job found");
    }
  };

  if (!s) return <div className="p-8 text-gray-500 font-mono text-sm">Loading…</div>;

  const save = async () => {
    const up = { live_mode: s.live_mode };
    const r = await api.updateSettings(up);
    setS(r);
    toast.success("Settings saved");
  };

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <Gear size={22} weight="bold" className="text-[#E3000B]" />
        <h1 className="font-head text-2xl font-black uppercase tracking-tighter" data-testid="settings-title">Configuration</h1>
      </div>

      <SitewideRules />

      <PublicSiteSettings />

      <NavLayoutSection />

      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between">
          <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">API Diagnostics</span>
          <Button onClick={runDiag} disabled={diagLoading} variant="outline" className="h-7 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-xs font-mono hover:border-[#E3000B] hover:text-[#E3000B]" data-testid="diag-refresh">
            <ArrowsClockwise size={11} className={`mr-1 ${diagLoading ? "animate-spin" : ""}`} /> RECHECK
          </Button>
        </div>
        <div className="p-4 font-mono text-xs space-y-2">
          {!diag && <div className="text-gray-500">Running checks…</div>}
          {diag && (
            <>
              <DiagRow label="Container outbound IP" value={diag.container_ip} mono />
              <DiagRow
                label="Keepa API"
                value={diag.keepa?.status === "ok" ? `OK · ${diag.keepa.tokens_left} tokens left` : diag.keepa?.status}
                ok={diag.keepa?.status === "ok"}
                warn={diag.keepa?.status === "tokens_exhausted"}
                error={diag.keepa?.error}
                testId="diag-keepa"
              />
              <DiagRow
                label="BrickLink API (OAuth)"
                value={diag.bricklink?.status === "ok" ? "OK" : diag.bricklink?.status}
                ok={diag.bricklink?.status === "ok"}
                error={diag.bricklink?.error}
                testId="diag-bricklink"
              />
              <DiagRow
                label="BrickLink Public Scraper"
                value={diag.bricklink_scraper?.status === "ok" ? "OK · fallback active" : diag.bricklink_scraper?.status}
                ok={diag.bricklink_scraper?.status === "ok"}
                error={diag.bricklink_scraper?.error}
                testId="diag-bricklink-scraper"
              />
              {diag.bricklink?.error && diag.bricklink.error.includes("TOKEN_IP_MISMATCHED") && diag.bricklink_scraper?.status === "ok" && (
                <div className="mt-3 p-3 border border-[#00A850]/40 bg-[#00A850]/10 rounded-sm text-gray-900">
                  <div className="text-[#00A850] font-bold uppercase mb-1">Scraper Fallback Active</div>
                  <div className="text-[11px] leading-relaxed text-gray-800">
                    OAuth API is blocked by IP whitelist, but the public BrickLink price-guide
                    scraper is fetching real data successfully. To unblock OAuth (faster + per-country
                    breakdown), add IP <span className="text-[#E3000B] font-bold">{diag.container_ip}</span>{" "}
                    to your BrickLink consumer-key whitelist.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between">
          <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600 flex items-center gap-2">
            <MagicWand size={14} weight="bold" className="text-[#E3000B]" /> ASIN Resolver
          </span>
          <button onClick={() => loadResolverStats(resolverTheme)} className="text-gray-500 hover:text-[#E3000B]" data-testid="resolver-refresh">
            <ArrowsClockwise size={11} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs font-mono text-gray-600 leading-relaxed">
            One-time bulk Keepa resolution for "LEGO {`{set_number}`}" across your catalog.
            Cost on this Keepa plan: <span className="text-[#E3000B]">~10 tokens</span> per
            set without an ASIN hint, <span className="text-[#00A850]">~2 tokens</span> when
            a hint exists. <span className="text-[#E3000B]">Checkpoint mode</span> (default)
            skips any set already attempted, so re-running the same theme + batch picks up
            where you left off. Successful resolves immediately push live Amazon data into
            the Opportunity Board (no separate Live Scan needed).
          </div>

          {resolverStats && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.1em] text-gray-500">
                  {resolverStats.theme ? `Counts for theme: ` : "Catalog totals"}
                  {resolverStats.theme && (
                    <span className="text-[#E3000B] ml-1" data-testid="resolver-stats-scope">"{resolverStats.theme}"</span>
                  )}
                </span>
                {resolverStats.theme && resolverStats.eligible_to_scan > 0 && (
                  <span className="font-mono text-[10px] text-gray-500" data-testid="resolver-stats-eligible-tip">
                    Next batch will pull <span className="text-[#E3000B]">{Math.min(resolverStats.eligible_to_scan, Number(resolverLimit) || 50).toLocaleString()}</span>
                    {" "}of <span className="text-[#E3000B]">{resolverStats.eligible_to_scan.toLocaleString()}</span> eligible
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[#E4E4E0] rounded-sm">
                <ResolverStat label={resolverStats.theme ? "Sets in theme" : "Total in catalog"}
                  value={resolverStats.total_sets.toLocaleString()} />
                <ResolverStat label="Keepa verified" value={resolverStats.verified_via_keepa.toLocaleString()} tone="pos" />
                <ResolverStat label="Not on Amazon" value={(resolverStats.not_listed_via_keepa ?? 0).toLocaleString()} tone="neg" />
                <ResolverStat label="Attempted" value={resolverStats.attempted.toLocaleString()} tone="accent" />
                <ResolverStat label="Eligible to scan" value={(resolverStats.eligible_to_scan ?? resolverStats.remaining).toLocaleString()} tone="warn" />
              </div>
            </>
          )}

          {resolverStats && resolverStats.keepa_tokens_left != null && (
            <div className="border border-[#E4E4E0] rounded-sm px-3 py-2 flex items-center justify-between font-mono text-[11px]" data-testid="resolver-tokens">
              <span className="text-gray-500 uppercase tracking-wider">Keepa tokens</span>
              <span className="text-[#E3000B]">
                {resolverStats.keepa_tokens_left} left
                {resolverStats.keepa_refill_rate ? ` · refilling ${resolverStats.keepa_refill_rate}/cycle` : ""}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Theme (optional)</Label>
              <select value={resolverTheme} onChange={e => setResolverTheme(e.target.value)}
                className="w-full h-9 bg-[#F6F6F3] border border-[#E4E4E0] text-xs font-mono px-2 text-gray-800 rounded-sm"
                data-testid="resolver-theme">
                <option value="">All themes</option>
                {themesList.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Batch size</Label>
              <Input type="number" value={resolverLimit} min={5} max={5000}
                onChange={e => setResolverLimit(e.target.value)}
                className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-sm"
                data-testid="resolver-limit" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={runResolver}
                disabled={resolverJob && ["running", "waiting_for_tokens"].includes(resolverJob.status)}
                className="flex-1 h-9 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800] disabled:opacity-50"
                data-testid="resolver-run">
                <Play size={12} weight="fill" className="mr-1" />
                {resolverJob && ["running", "waiting_for_tokens"].includes(resolverJob.status) ? "RUNNING" : "RESOLVE"}
              </Button>
              {resolverJob && ["running", "waiting_for_tokens"].includes(resolverJob.status) && (
                <Button onClick={cancelResolver}
                  variant="outline"
                  className="h-9 rounded-sm border-[#FF3333] bg-transparent text-[#FF3333] font-bold uppercase tracking-wider text-xs hover:bg-[#FF3333]/10"
                  data-testid="resolver-cancel">
                  STOP
                </Button>
              )}
            </div>
          </div>

          {resumableJob && (!resolverJob || ["completed", "failed", "paused"].includes(resolverJob?.status)) && (
            <div className="flex items-center justify-between border border-[#0055BF]/40 bg-[#0055BF]/[0.05] rounded-sm px-3 py-2" data-testid="resolver-resume-row">
              <div>
                <div className="text-xs text-gray-900 flex items-center gap-2">
                  <ArrowsClockwise size={12} weight="bold" className="text-[#0055BF]" />
                  Resume previous run?
                </div>
                <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                  Last job: theme=<span className="text-[#0055BF]">{resumableJob.params?.theme || "all"}</span>,
                  limit=<span className="text-[#0055BF]">{resumableJob.params?.limit}</span>,
                  status=<span className="text-[#FF8C00]">{resumableJob.status}</span>
                  {resumableJob.params?.tokens_consumed != null && <> · spent {resumableJob.params.tokens_consumed} tokens</>}
                  . Picks up exactly where it stopped — already-attempted sets are skipped.
                </div>
              </div>
              <Button onClick={resumeResolver}
                className="h-8 rounded-sm bg-[#0055BF] text-gray-900 font-bold uppercase tracking-wider text-xs hover:bg-[#0099E0]"
                data-testid="resolver-resume">
                <Play size={11} weight="fill" className="mr-1" />
                RESUME
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between border border-[#E4E4E0] rounded-sm px-3 py-2">
            <div>
              <div className="text-xs text-gray-900">Continuous mode</div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                Auto-sleep when tokens run low and resume on refill — set a large batch (e.g. 5000)
                and let it churn overnight.
              </div>
            </div>
            <Switch checked={resolverContinuous} onCheckedChange={setResolverContinuous}
              className="data-[state=checked]:bg-[#E3000B]" data-testid="resolver-continuous" />
          </div>

          <div className="flex items-center justify-between border border-[#E4E4E0] rounded-sm px-3 py-2">
            <div>
              <div className="text-xs text-gray-900">Retry previously-failed sets</div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                Off (default): skip everything already attempted — pure checkpoint.
                On: re-attempt sets that came back as not-on-Amazon last time
                (useful after Amazon adds new listings).
              </div>
            </div>
            <Switch checked={resolverRetryFailed} onCheckedChange={setResolverRetryFailed}
              className="data-[state=checked]:bg-[#FF8C00]" data-testid="resolver-retry-failed" />
          </div>

          <div className="flex items-center justify-between border border-[#E4E4E0] rounded-sm px-3 py-2">
            <div>
              <div className="text-xs text-gray-900">Also refresh BrickLink (US, sealed)</div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                On (default): each resolved set is upgraded to fully <span className="text-[#00A850]">live</span> data
                (data_source = "live" instead of "partial"). Adds ~1s/set + counts against
                BrickLink's anonymous rate limit. Disable if BL keeps 429-ing.
              </div>
            </div>
            <Switch checked={resolverFetchBL} onCheckedChange={setResolverFetchBL}
              className="data-[state=checked]:bg-[#00A850]" data-testid="resolver-fetch-bricklink" />
          </div>

          {resolverJob && (
            <div className="border border-[#E4E4E0] rounded-sm p-3 font-mono text-[11px]" data-testid="resolver-job">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500">JOB STATUS</span>
                <span className={
                  resolverJob.status === "completed" ? "text-[#00A850]" :
                  resolverJob.status === "failed" ? "text-[#FF3333]" :
                  resolverJob.status === "paused" ? "text-[#FF8C00]" :
                  resolverJob.status === "waiting_for_tokens" ? "text-[#FF8C00]" : "text-[#E3000B]"
                }>{resolverJob.status.toUpperCase().replace(/_/g, " ")}</span>
              </div>
              <div className="w-full h-1.5 bg-[#F6F6F3] border border-[#E4E4E0] rounded-sm overflow-hidden">
                <div className="h-full bg-[#E3000B] transition-all"
                  style={{ width: `${resolverJob.total ? Math.round(resolverJob.progress / resolverJob.total * 100) : 0}%` }} />
              </div>
              <div className="flex justify-between mt-2 text-gray-600">
                <span>{resolverJob.progress}/{resolverJob.total} processed</span>
                <span className="text-[#00A850]">{resolverJob.opportunities_found} resolved</span>
              </div>
              {resolverJob.params?.tokens_consumed != null && (
                <div className="flex justify-between mt-1 text-gray-500">
                  <span>Tokens spent: {resolverJob.params.tokens_consumed}</span>
                  {resolverJob.params.tokens_left != null && <span>Tokens left: {resolverJob.params.tokens_left}</span>}
                </div>
              )}
              {resolverJob.error && <div className="text-gray-500 mt-1">{resolverJob.error}</div>}
            </div>
          )}

          <ReverifyBlock />
        </div>
      </div>

      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between">
          <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">Catalog Import</span>
          <button onClick={onClearExtra} className="text-gray-500 hover:text-[#FF3333] flex items-center gap-1 text-[10px] font-mono uppercase" data-testid="catalog-clear">
            <Trash size={11} /> Reset to demo
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs font-mono text-gray-600 leading-relaxed">
            Upload a CSV of LEGO sets to expand the catalog. Larger catalog = more arbitrage
            opportunities to scan. Required columns: <span className="text-[#E3000B]">set_number, name, theme</span>.
            Optional but recommended: <span className="text-[#00A850]">asin</span> (fixes the Amazon
            buy-box mismatch directly), year, pieces, msrp, retired, image_url.
          </div>

          <label className={`flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
            uploading ? "border-[#E3000B] bg-[#E3000B]/10" : "border-[#E4E4E0] hover:border-[#E3000B]"
          }`} data-testid="catalog-upload-label">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onUpload}
              disabled={uploading}
              className="hidden"
              data-testid="catalog-upload-input"
            />
            <FileCsv size={16} className="text-[#E3000B]" weight="bold" />
            <span className="font-mono text-xs uppercase tracking-wider text-gray-800">
              {uploading ? "Uploading…" : "Click to upload CSV"}
            </span>
          </label>

          {uploadResult && (
            <div className="border border-[#E4E4E0] rounded-sm p-3 font-mono text-[11px] space-y-1" data-testid="upload-result">
              <div><span className="text-gray-500">INSERTED:</span> <span className="text-[#00A850]">{uploadResult.inserted}</span></div>
              <div><span className="text-gray-500">UPDATED:</span> <span className="text-[#E3000B]">{uploadResult.updated}</span></div>
              <div><span className="text-gray-500">SKIPPED:</span> <span className="text-gray-800">{uploadResult.skipped}</span></div>
              <div><span className="text-gray-500">TOTAL IN CATALOG:</span> <span className="text-gray-900 font-bold">{uploadResult.total_in_db}</span></div>
              {uploadResult.errors?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#E4E4E0]">
                  <div className="text-[#FF3333] mb-1">FIRST {uploadResult.errors.length} ERRORS:</div>
                  {uploadResult.errors.map((e, i) => <div key={i} className="text-gray-500">· {e}</div>)}
                </div>
              )}
              <div className="mt-2 text-[10px] text-gray-500">
                Tip: now go to <span className="text-[#E3000B]">Scan Engine → run a Theme or Set-List scan</span> with Live Mode ON to fetch real prices for the new sets.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border border-[#E4E4E0] rounded-sm">
        <div className="px-4 py-3 border-b border-[#E4E4E0] font-head font-bold uppercase text-xs tracking-wider text-gray-600">Data Source Mode</div>
        <div className="p-4 flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-900">Live Mode</div>
            <div className="text-xs font-mono text-gray-500 mt-1">When ON, scans call Keepa + BrickLink. When OFF, uses seeded/mock data for demo.</div>
          </div>
          <Switch checked={!!s.live_mode} onCheckedChange={v => set("live_mode", v)} className="data-[state=checked]:bg-[#E3000B]" data-testid="settings-live-mode" />
        </div>
      </div>

      <ApiKeysSection />

      <ScanCacheSection />

      <Button onClick={save} className="h-9 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800]" data-testid="settings-save">
        SAVE SETTINGS
      </Button>
    </div>
  );
}

/**
 * ScanCacheSection
 * ----------------
 * The 24h scan cache saves upstream calls (Keepa, BrickLink, SerpApi) — but
 * if an upstream was broken when we cached, we'll keep serving that empty
 * result for the rest of the window. This panel lets the user see how many
 * rows are "poisoned" (all-empty) and clear them without touching useful
 * cached data.
 */
function ScanCacheSection() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { setStats(await api.scanCacheStats()); } catch {} };
  useEffect(() => { load(); }, []);

  const clearPoisoned = async (source) => {
    setBusy(true);
    try {
      const r = await api.scanCacheClear({ source: source || null, only_poisoned: true });
      toast.success(`Removed ${r.deleted} empty cache row(s)`);
      await load();
    } catch { toast.error("Clear failed"); }
    setBusy(false);
  };

  const clearAll = async (source) => {
    if (!window.confirm(`Clear ALL cached ${source || "scan"} snapshots? Next scan will refetch fresh data (uses upstream credits).`)) return;
    setBusy(true);
    try {
      const r = await api.scanCacheClear({ source: source || null, only_poisoned: false });
      toast.success(`Removed ${r.deleted} cache row(s)`);
      await load();
    } catch { toast.error("Clear failed"); }
    setBusy(false);
  };

  const poisonedTotal = stats?.poisoned_by_source
    ? Object.values(stats.poisoned_by_source).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="border border-[#E4E4E0] rounded-sm" data-testid="scan-cache-section">
      <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between">
        <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">Scan Cache (24h)</span>
        <button onClick={load} className="text-gray-500 hover:text-[#E3000B]" data-testid="cache-refresh">
          <ArrowsClockwise size={11} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div className="text-[11px] font-mono text-gray-500 leading-relaxed">
          The unified cache saves Keepa/BrickLink/SerpApi round-trips for 24h. If an upstream fails
          silently while cached, the empty result "poisons" the row — every downstream lookup returns
          nothing until it expires. Clear poisoned rows below to force fresh fetches.
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border border-[#E4E4E0] rounded-sm">
            <CacheStat label="Total cached" value={stats.total.toLocaleString()} />
            {Object.entries(stats.by_source || {}).map(([src, n]) => (
              <CacheStat key={src} label={src} value={n.toLocaleString()} />
            ))}
            <CacheStat
              label="Poisoned rows"
              value={poisonedTotal.toLocaleString()}
              tone={poisonedTotal > 0 ? "warn" : "pos"}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            onClick={() => clearPoisoned(null)}
            disabled={busy || poisonedTotal === 0}
            className="h-8 rounded-sm bg-[#FF8C00] text-gray-900 font-bold uppercase tracking-wider text-[10px] hover:bg-[#E67D00] disabled:opacity-40"
            data-testid="clear-poisoned"
          >
            {busy ? "…" : `CLEAR ${poisonedTotal} POISONED`}
          </Button>
          <Button
            onClick={() => clearAll("bricklink")}
            disabled={busy}
            variant="outline"
            className="h-8 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-[10px] font-mono uppercase text-gray-800 hover:border-[#E3000B] hover:text-[#E3000B]"
            data-testid="clear-bricklink-cache"
          >
            NUKE BRICKLINK CACHE
          </Button>
          <Button
            onClick={() => clearAll("amazon")}
            disabled={busy}
            variant="outline"
            className="h-8 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-[10px] font-mono uppercase text-gray-800 hover:border-[#E3000B] hover:text-[#E3000B]"
            data-testid="clear-amazon-cache"
          >
            NUKE AMAZON CACHE
          </Button>
          <Button
            onClick={() => clearAll(null)}
            disabled={busy}
            variant="outline"
            className="h-8 rounded-sm border-[#FF3333] bg-transparent text-[#FF3333] font-bold uppercase text-[10px] tracking-wider hover:bg-[#FF3333]/10"
            data-testid="clear-all-cache"
          >
            NUKE ALL
          </Button>
        </div>
      </div>
    </div>
  );
}

function CacheStat({ label, value, tone }) {
  const tones = { pos: "text-[#00A850]", warn: "text-[#FF8C00]", neg: "text-[#FF3333]" };
  const cls = tones[tone] || "text-gray-900";
  return (
    <div className="border-r border-[#E4E4E0] last:border-r-0 px-3 py-2">
      <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-0.5">{label}</div>
      <div className={`font-mono text-sm font-bold ${cls}`}>{value}</div>
    </div>
  );
}

/**
 * ApiKeysSection
 * ---------------
 * In-app editor for BrickLink, Keepa, and SerpApi credentials. Values are
 * masked when returned by the backend; the UI only sends fields the user
 * actually types into. A per-provider "Test" button pings the live API so the
 * user knows immediately whether their tokens work.
 */
function ApiKeysSection() {
  const [keys, setKeys] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [tests, setTests] = useState({}); // { bricklink: {ok, message, loading} }
  const [ip, setIp] = useState(null);
  const [revealed, setRevealed] = useState({}); // { [key]: bool }

  const load = async () => {
    try { setKeys(await api.getApiKeys()); } catch { toast.error("Failed to load API keys"); }
    try { const d = await api.diagnostics(); setIp(d?.container_ip || null); } catch {}
  };
  useEffect(() => { load(); }, []);

  const copyIp = async () => {
    if (!ip) return;
    try { await navigator.clipboard.writeText(ip); toast.success(`Copied ${ip}`); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const setEdit = (k, v) => setEdits((s) => ({ ...s, [k]: v }));

  const saveKeys = async () => {
    const payload = {};
    for (const [k, v] of Object.entries(edits)) {
      if (v && v.trim()) payload[k] = v.trim();
    }
    if (!Object.keys(payload).length) {
      toast.info("Nothing to save — leave a field blank to keep the existing value");
      return;
    }
    setSaving(true);
    try {
      const r = await api.updateApiKeys(payload);
      toast.success(`Updated ${r.updated.length} key(s)`);
      setEdits({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const testProvider = async (provider) => {
    setTests((s) => ({ ...s, [provider]: { loading: true } }));
    try {
      const r = await api.testApiKey(provider);
      setTests((s) => ({ ...s, [provider]: { ok: r.ok, message: r.message } }));
      if (r.ok) toast.success(`${provider.toUpperCase()}: ${r.message}`);
      else toast.error(`${provider.toUpperCase()}: ${r.message}`);
    } catch (e) {
      setTests((s) => ({ ...s, [provider]: { ok: false, message: "Request failed" } }));
      toast.error(`${provider.toUpperCase()}: request failed`);
    }
  };

  const groups = [
    {
      provider: "bricklink",
      title: "BrickLink (OAuth1)",
      hint: "Get keys at bricklink.com → API → Consumer registration. Whitelist your outbound IP shown above.",
      fields: [
        { key: "BRICKLINK_CONSUMER_KEY", label: "Consumer Key" },
        { key: "BRICKLINK_CONSUMER_SECRET", label: "Consumer Secret" },
        { key: "BRICKLINK_TOKEN_VALUE", label: "Token Value" },
        { key: "BRICKLINK_TOKEN_SECRET", label: "Token Secret" },
      ],
    },
    {
      provider: "keepa",
      title: "Keepa",
      hint: "Get key at keepa.com/#!api. One key covers Amazon Buy Box + history.",
      fields: [{ key: "KEEPA_ACCESS_KEY", label: "Access Key" }],
    },
    {
      provider: "serpapi",
      title: "SerpApi (eBay lookups)",
      hint: "Get key at serpapi.com/manage-api-key. Free tier = 250 searches/month.",
      fields: [{ key: "SERPAPI_KEY", label: "API Key" }],
    },
    {
      provider: "pricecharting",
      title: "PriceCharting (sealed / used LEGO prices)",
      hint: "Pro subscription key from pricecharting.com/api-documentation. Used by the Deals SCAN PRICECHARTING button.",
      fields: [{ key: "PRICECHARTING_KEY", label: "API Token" }],
    },
  ];

  return (
    <div className="border border-[#E4E4E0] rounded-sm" data-testid="api-keys-section">
      <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center justify-between">
        <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">API Keys</span>
        <span className="text-[10px] font-mono text-gray-400">Values are stored in backend/.env · hot-applied</span>
      </div>
      <div className="p-4 space-y-6">
        <div className="text-[11px] font-mono text-gray-500 leading-relaxed">
          Rotate provider credentials without touching the server. Existing values are masked;
          leave a field blank to keep it unchanged. Backend picks up new keys on the next request —
          no restart needed.
        </div>

        <div
          className="border border-[#E3000B]/60 bg-[#E3000B]/[0.06] rounded-sm p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          data-testid="outbound-ip-banner"
        >
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#E3000B] mb-1">
              This container's outbound IP
            </div>
            <div className="font-mono text-lg text-gray-900 select-all" data-testid="outbound-ip-value">
              {ip || "detecting…"}
            </div>
            <div className="text-[10px] font-mono text-gray-600 mt-1 leading-relaxed">
              Paste this into BrickLink → <span className="text-[#E3000B]">My Account → API → Consumers → your consumer key → IP whitelist</span>.
              Once saved, come back here and hit <span className="text-[#E3000B]">TEST</span> on BrickLink below.
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              onClick={copyIp}
              disabled={!ip}
              variant="outline"
              className="h-8 rounded-sm border-[#E3000B] bg-transparent text-[#E3000B] font-bold uppercase text-[10px] tracking-wider hover:bg-[#E3000B]/10 disabled:opacity-40"
              data-testid="copy-outbound-ip"
            >
              COPY IP
            </Button>
            <a
              href="https://www.bricklink.com/v2/api/register_consumer.page"
              target="_blank"
              rel="noreferrer"
              className="h-8 px-3 flex items-center rounded-sm border border-[#E4E4E0] bg-[#F6F6F3] text-[10px] font-mono uppercase tracking-wider text-gray-800 hover:border-[#E3000B] hover:text-[#E3000B]"
              data-testid="open-bricklink-console"
            >
              OPEN BRICKLINK
            </a>
          </div>
        </div>

        {!keys && <div className="text-xs font-mono text-gray-500">Loading…</div>}
        {keys && groups.map((g) => {
          const t = tests[g.provider];
          return (
            <div key={g.provider} className="border border-[#E4E4E0] rounded-sm" data-testid={`api-keys-group-${g.provider}`}>
              <div className="px-3 py-2 border-b border-[#E4E4E0] flex items-center justify-between">
                <div>
                  <div className="font-head font-bold uppercase text-[11px] tracking-wider text-gray-900">{g.title}</div>
                  <div className="text-[10px] font-mono text-gray-400 mt-0.5">{g.hint}</div>
                </div>
                <Button
                  onClick={() => testProvider(g.provider)}
                  disabled={t?.loading}
                  variant="outline"
                  className="h-7 rounded-sm border-[#E4E4E0] bg-[#FFFFFF] text-[10px] font-mono uppercase hover:border-[#E3000B] hover:text-[#E3000B]"
                  data-testid={`test-${g.provider}`}
                >
                  {t?.loading ? "TESTING…" : "TEST"}
                </Button>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {g.fields.map((f) => {
                  const cur = keys.keys?.[f.key];
                  const editVal = edits[f.key] ?? "";
                  const isRevealed = !!revealed[f.key];
                  const expectedLen = 32; // BrickLink+Keepa are all fixed lengths; SerpApi varies but we don't enforce
                  const showLen = editVal.length > 0;
                  const lenOk = f.key.startsWith("BRICKLINK") ? editVal.length === expectedLen : true;
                  return (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 flex items-center justify-between">
                        <span>{f.label}</span>
                        {cur?.configured
                          ? <span className="text-[9px] text-[#00A850] font-mono normal-case">SET · {cur.masked}</span>
                          : <span className="text-[9px] text-[#FF3333] font-mono normal-case">MISSING</span>}
                      </Label>
                      <div className="relative">
                        <Input
                          type={isRevealed ? "text" : "password"}
                          autoComplete="new-password"
                          spellCheck={false}
                          placeholder={cur?.configured ? "•••• leave blank to keep ••••" : "paste new value"}
                          value={editVal}
                          onChange={(e) => setEdit(f.key, e.target.value.trim())}
                          className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs pr-14"
                          data-testid={`input-${f.key}`}
                        />
                        <button
                          type="button"
                          onClick={() => setRevealed((s) => ({ ...s, [f.key]: !s[f.key] }))}
                          className="absolute right-1 top-1 h-7 px-2 text-[9px] font-mono uppercase text-gray-500 hover:text-[#E3000B]"
                          data-testid={`reveal-${f.key}`}
                        >
                          {isRevealed ? "HIDE" : "SHOW"}
                        </button>
                      </div>
                      {showLen && (
                        <div className={`text-[9px] font-mono ${lenOk ? "text-gray-500" : "text-[#FF3333]"}`}>
                          {editVal.length} chars {f.key.startsWith("BRICKLINK") ? `(expected ${expectedLen})` : ""}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {t && !t.loading && (
                <div
                  className={`px-3 pb-3 text-[11px] font-mono ${t.ok ? "text-[#00A850]" : "text-[#FF3333]"}`}
                  data-testid={`test-result-${g.provider}`}
                >
                  {t.ok ? "✓ " : "✗ "}{t.message}
                </div>
              )}
            </div>
          );
        })}

        <div className="flex items-center justify-between pt-2 border-t border-[#E4E4E0]">
          <div className="text-[10px] font-mono text-gray-400">
            {Object.keys(edits).filter(k => edits[k]?.trim()).length} field(s) with pending changes
          </div>
          <Button
            onClick={saveKeys}
            disabled={saving}
            className="h-9 rounded-sm bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800] disabled:opacity-50"
            data-testid="save-api-keys"
          >
            {saving ? "SAVING…" : "SAVE KEYS"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, hint, ...rest }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</Label>
      <Input value={value ?? ""} onChange={e => onChange(e.target.value)}
        className="h-9 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-sm" data-testid={testId} {...rest} />
      {hint && <div className="text-[10px] font-mono text-gray-400">{hint}</div>}
    </div>
  );
}

function DiagRow({ label, value, ok, warn, error, mono, testId }) {
  let icon = <span className="w-3 inline-block" />;
  let cls = "text-gray-800";
  if (ok) { icon = <CheckCircle size={12} weight="fill" className="text-[#00A850] inline" />; cls = "text-[#00A850]"; }
  else if (warn) { icon = <Warning size={12} weight="fill" className="text-[#E3000B] inline" />; cls = "text-[#E3000B]"; }
  else if (value && value !== "unknown" && value !== "ok") { icon = <XCircle size={12} weight="fill" className="text-[#FF3333] inline" />; cls = "text-[#FF3333]"; }
  return (
    <div data-testid={testId} className="flex items-center justify-between gap-3 border-b border-[#F6F6F3] pb-2">
      <span className="text-gray-500 uppercase text-[10px] tracking-wider">{label}</span>
      <span className={`${cls} ${mono ? "font-mono" : ""} text-right`}>
        {icon} <span className="ml-1">{value ?? "—"}</span>
        {error && <div className="text-[10px] text-[#FF3333] mt-0.5 font-normal">{String(error).substring(0, 120)}</div>}
      </span>
    </div>
  );
}

function ResolverStat({ label, value, tone }) {
  const tones = {
    pos: "text-[#00A850]",
    accent: "text-[#E3000B]",
    warn: "text-[#FF8C00]",
    neg: "text-[#FF3333]",
  };
  const cls = tones[tone] || "text-gray-900";
  return (
    <div className="border-r border-[#E4E4E0] last:border-r-0 px-3 py-2">
      <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-0.5">{label}</div>
      <div className={`font-mono text-sm font-bold ${cls}`}>{value}</div>
    </div>
  );
}

/**
 * ReverifyBlock — re-checks already-verified ASINs against the new
 * strict matcher and clears bad ones so the next resolver run fixes them.
 */
function ReverifyBlock() {
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(500);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.asinReverify({ limit: Number(limit) || 500, dry_run: dryRun });
      setResult(r);
      if (dryRun) toast.info(`Dry run: ${r.checked} checked, ${r.kept} kept, ${r.sample_bad.length} would clear`);
      else toast.success(`Re-verify done: ${r.kept} kept, ${r.cleared} cleared. Re-run ASIN resolver to fix them.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-verify failed");
    }
    setBusy(false);
  };

  return (
    <div className="border border-[#FF3333]/30 rounded-sm p-3 mt-3" data-testid="reverify-block">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-mono text-[#FF3333]">Re-verify saved ASINs</div>
          <div className="text-[10px] font-mono text-gray-500 leading-relaxed mt-0.5">
            Old matcher accepted some wrong ASINs (e.g. 40085 matched to a Star Wars set).
            This sweeps every Keepa-verified ASIN, checks the title against the tightened
            matcher, and clears bad ones so the next ASIN scan re-resolves them correctly.
            <br />Cost: ~1 Keepa token per set. Start with a small limit + dry-run to preview.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-[10px] font-mono uppercase text-gray-500">Batch</Label>
        <Input type="number" value={limit} min={10} max={5000}
          onChange={e => setLimit(e.target.value)}
          className="h-8 w-24 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs"
          data-testid="reverify-limit" />
        <Button
          onClick={() => run(true)}
          disabled={busy}
          variant="outline"
          className="h-8 rounded-sm border-[#E4E4E0] bg-[#F6F6F3] text-[10px] font-mono uppercase text-gray-800 hover:border-[#E3000B] hover:text-[#E3000B]"
          data-testid="reverify-dry"
        >
          DRY RUN
        </Button>
        <Button
          onClick={() => run(false)}
          disabled={busy}
          className="h-8 rounded-sm bg-[#FF3333] text-gray-900 font-bold uppercase tracking-wider text-[10px] hover:bg-[#FF5555] disabled:opacity-50"
          data-testid="reverify-run"
        >
          {busy ? "CHECKING…" : "PURGE BAD ASINS"}
        </Button>
      </div>
      {result && (
        <div className="mt-2 border border-[#E4E4E0] rounded-sm p-2 font-mono text-[10px] space-y-0.5" data-testid="reverify-result">
          <div><span className="text-gray-500">Checked:</span> <span className="text-gray-900">{result.checked}</span></div>
          <div><span className="text-gray-500">Kept:</span> <span className="text-[#00A850]">{result.kept}</span></div>
          <div><span className="text-gray-500">Cleared:</span> <span className="text-[#FF3333]">{result.cleared}</span> {result.dry_run && <span className="text-gray-400">(dry run — nothing actually deleted)</span>}</div>
          <div><span className="text-gray-500">Unreachable:</span> <span className="text-gray-600">{result.unreachable}</span></div>
          <div><span className="text-gray-500">Tokens spent:</span> <span className="text-[#E3000B]">{result.tokens_consumed}</span></div>
          {result.sample_bad?.length > 0 && (
            <div className="mt-1 pt-1 border-t border-[#E4E4E0]">
              <div className="text-gray-500 mb-0.5">Sample mismatches:</div>
              {result.sample_bad.slice(0, 8).map((b, i) => (
                <div key={i} className="text-gray-600 truncate">
                  <span className="text-[#E3000B]">{b.set_number}</span> · <span className="text-[#FF3333]">{b.asin}</span> · {(b.title || "").substring(0, 70)}
                </div>
              ))}
              {result.sample_bad.length > 8 && (
                <div className="text-gray-400">…{result.sample_bad.length - 8} more</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
